import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { rootApi } from "./lib/api/rootApi";
import { attendanceApi } from "./features/attendance/api/attendanceApi";
import { teacherDashboardApi } from "./features/teacherDashboard/api/teacherDashboardApi";
import { infoBoardApi } from "./features/bulletin-board/api/bulletinApi";
import { flexbandApi } from "./features/flexband/api/flexbandApi";
import { monitorApi } from "./features/settings/monitore/api/monitorApi";
import { waitlistApi } from "./features/course-management/course-lists/api/waitlistApi";
import { liveOverviewApi } from "./features/klassenuebersicht/klassenuebersicht/api/liveOverviewApi";
import { parentPortalApi } from "./features/parent-portal/api/parentPortalApi";
import { notificationsApi } from "./features/notifications/api/notificationsApi";
import { calendarApi } from "./features/calendar/api/calendarApi";
// Side-effect imports: register injected endpoints with the base API
import "./features/notifications/api/notificationsQueries";
import "./features/notifications/api/notificationsMutations";
import "./lib/api/staffEndpoints";
import "./features/settings/mitarbeiter/api/staffApi";
import "./features/checkin-checkout/api/checkinCheckoutEndpoints";
import "./features/attendance/api/fehlzeitenEndpoints";
import "./features/attendance/api/timelineEndpoints";
import "./features/attendance/api/lessonAttendanceEndpoints";
import "./features/klassenuebersicht/klassenuebersicht/api/semesterEndpoints";
import "./features/klassenuebersicht/klassenuebersicht/api/overviewEndpoints";
import "./features/klassenuebersicht/klassenuebersicht/api/klassenlisteEndpoints";
import "./features/klassenuebersicht/klassenuebersicht/api/tableViewEndpoints";
import "./features/course-management/shared/api/courseMutationsApi";
import "./features/course-management/overview-shell/api/registrationApi";
import "./features/admin-dashboard/api/newsfeedApi";
import "./features/admin-dashboard/api/adminDashboardApi";
import "./features/auth/api/authApi";
import { markMutationFired } from "./features/substitute-planning/utils/realtimeDebounce";
import type { Middleware } from "@reduxjs/toolkit";

/**
 * Middleware: marks mutation timestamps so the Supabase Realtime handler
 * skips redundant cache invalidation (prevents double-fetching).
 */
const realtimeDebounceMiddleware: Middleware =
  () => (next) => (action: any) => {
    // Detect any RTK Query mutation pending action
    if (action?.type?.endsWith("/executeMutation/pending")) {
      markMutationFired();
    }
    return next(action);
  };

export const store = configureStore({
  reducer: {
    [rootApi.reducerPath]: rootApi.reducer,
    [attendanceApi.reducerPath]: attendanceApi.reducer,
    [teacherDashboardApi.reducerPath]: teacherDashboardApi.reducer,
    [infoBoardApi.reducerPath]: infoBoardApi.reducer,
    [flexbandApi.reducerPath]: flexbandApi.reducer,
    [monitorApi.reducerPath]: monitorApi.reducer,
    [waitlistApi.reducerPath]: waitlistApi.reducer,
    [liveOverviewApi.reducerPath]: liveOverviewApi.reducer,
    [parentPortalApi.reducerPath]: parentPortalApi.reducer,
    [notificationsApi.reducerPath]: notificationsApi.reducer,
    [calendarApi.reducerPath]: calendarApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      realtimeDebounceMiddleware,
      rootApi.middleware,
      attendanceApi.middleware,
      teacherDashboardApi.middleware,
      infoBoardApi.middleware,
      flexbandApi.middleware,
      monitorApi.middleware,
      waitlistApi.middleware,
      liveOverviewApi.middleware,
      parentPortalApi.middleware,
      notificationsApi.middleware,
      calendarApi.middleware,
    ),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

setupListeners(store.dispatch);

/**
 * Reset all RTK Query API caches.
 * Call this on logout to prevent stale cross-school data.
 */
export const resetAllApiCaches = () => {
  store.dispatch(rootApi.util.resetApiState());
  store.dispatch(attendanceApi.util.resetApiState());
  store.dispatch(teacherDashboardApi.util.resetApiState());
  store.dispatch(infoBoardApi.util.resetApiState());
  store.dispatch(flexbandApi.util.resetApiState());
  store.dispatch(monitorApi.util.resetApiState());
  store.dispatch(waitlistApi.util.resetApiState());
  store.dispatch(liveOverviewApi.util.resetApiState());
  store.dispatch(parentPortalApi.util.resetApiState());
  store.dispatch(notificationsApi.util.resetApiState());
  store.dispatch(calendarApi.util.resetApiState());
  console.log("🗑️ All RTK Query caches cleared");
};
